import React, { useState, useEffect, useMemo } from 'react';

import _ from 'lodash';

import Box from '@tds/core-box';
import Heading from '@tds/core-heading';
import Text from '@tds/core-text';
import Radio from '@tds/core-radio';
import Input from '@tds/core-input';
import Select from '@tds/core-select';
import Button from '@tds/core-button';
import TextButton from '@tds/core-text-button';
import Tooltip from '@tds/core-tooltip';
import Notification from '@tds/core-notification';
import Spinner from '@tds/core-spinner';

import StatusBar from './statusBar';

import request from '../utils/request';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

const NewPhoneNumberCheck = () => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    portabilityTool: {
      headerCheckAvailibility,
      labelCheckAvailibility,
      labelRateCenter,
      labelRateCenterField,
      labelNpaNxx,
      labelProvince,
      noteRateCenter,
      labelNote,
      buttonClear,
      buttonCheck,
      errorMessage,
      successMessageWithDid,
      successMessageWithNoDid,
      toolTipRateCenter,
      toolTipNpaNxx
    }
  } = localizedStrings;

  const [stateValues, setStateValues] = useState({
    isLoading: true,
    type: '',
    province: '',
    rateCenter: '',
    npanxx: '',
    rateCenterData: '',
    rateCenterList: []
  });
  const [status, setStatus] = useState({
    isVisible: false,
    type: '',
    text: ''
  });

  useEffect(() => {
    request
      .get(`/rateCenter?country=CA&sortBy=RCLONG&sortOrder=ASC`)
      .then(response => {
        if (response && Array.isArray(response.data)) {
          setStateValues({
            ...stateValues,
            isLoading: false,
            rateCenterData: response.data
          });
        }
      });
  }, []);

  const rateCenterObjectMap = useMemo(
    () =>
      stateValues.rateCenterData &&
      stateValues.rateCenterData.reduce((acc, rc) => {
        const { npanxx, rclong, newDid, province } = rc;
        if (acc[province]) {
          if (!acc[province][rclong])
            acc[province][rclong] = {
              npanxxList: [],
              name: rclong,
              newDidCount: 0
            };
          if (newDid) acc[province][rclong].newDidCount += 1;
          acc[province][rclong].npanxxList.push({ npanxx, newDid });
        } else {
          acc[province] = {};
        }
        return acc;
      }, {}),
    [stateValues.rateCenterData]
  );

  const checkNewNpaNxx = phoneNumber => {
    let result = '';
    Object.keys(rateCenterObjectMap).forEach(province => {
      Object.keys(rateCenterObjectMap[province]).forEach(rateCenter => {
        const rcData = rateCenterObjectMap[province][rateCenter];
        const searchResult = _.find(rcData.npanxxList, {
          npanxx: phoneNumber
        });
        if (searchResult)
          result = {
            ...rcData
          };
      });
    });
    return result;
  };

  const handleProvinceChange = e => {
    const valueProvince = e.target.value;
    const list =
      valueProvince &&
      rateCenterObjectMap[valueProvince] &&
      Object.keys(rateCenterObjectMap[valueProvince]).map(
        rc =>
          rateCenterObjectMap[valueProvince][rc].newDidCount > 0 && {
            text: rateCenterObjectMap[valueProvince][rc].name,
            value: rc
          }
      );
    setStateValues({
      ...stateValues,
      province: valueProvince,
      rateCenterList: list.filter(item => item)
    });
  };

  const handleClearFields = () => {
    setStateValues({
      ...stateValues,
      province: '',
      rateCenter: '',
      npanxx: ''
    });
    setStatus({
      ...status,
      isVisible: false
    });
  };

  const handleCheck = () => {
    const searchResult = checkNewNpaNxx(stateValues.npanxx);
    if (searchResult) {
      if (searchResult.newDidCount > 0)
        setStatus({
          isVisible: true,
          type: 'success',
          text: `${successMessageWithDid} ${searchResult.name}`
        });
      else
        setStatus({
          isVisible: true,
          type: 'success',
          text: successMessageWithNoDid
        });
    } else
      setStatus({
        isVisible: true,
        type: 'error',
        text: errorMessage
      });
  };

  const handleSwitch = type => {
    setStateValues({
      ...stateValues,
      type,
      province: '',
      rateCenter: '',
      npanxx: ''
    });
    setStatus({
      ...status,
      isVisible: false
    });
  };

  return (
    <>
      {stateValues.isLoading ? (
        <Spinner spinning label="Loading ratecenter data..." />
      ) : (
        <Box between={3}>
          <Heading level="h2" tag="h3">
            {headerCheckAvailibility}
          </Heading>
          <Box tag="fieldset" between={2}>
            <legend>
              <Text bold size="medium">
                {labelCheckAvailibility}
              </Text>
            </legend>
            <div
              style={{
                display: 'flex'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  width: '13%',
                  justifyContent: 'space-between'
                }}
              >
                <Radio
                  label={labelRateCenter}
                  name="availibility-check"
                  value="Rate Center"
                  onClick={() => {
                    handleSwitch('RC');
                  }}
                />
                <Tooltip copy="en">{toolTipRateCenter}</Tooltip>
              </div>
              <div
                style={{
                  display: 'flex',
                  marginLeft: '3rem',
                  width: '11%',
                  justifyContent: 'space-between'
                }}
              >
                <Radio
                  label={labelNpaNxx}
                  name="availibility-check"
                  value="NPA NXX"
                  onClick={() => {
                    handleSwitch('NPANXX');
                  }}
                />
                <Tooltip copy="en">{toolTipNpaNxx}</Tooltip>
              </div>
            </div>
          </Box>
          {stateValues.type && (
            <Box tag="fieldset" between={2}>
              <div
                style={{
                  display: 'flex',
                  marginTop: '1rem'
                }}
              >
                {stateValues.type === 'RC' && (
                  <>
                    <div style={{ width: '20%' }}>
                      <Select
                        label={labelProvince}
                        placeholder="Please select..."
                        options={
                          stateValues.type
                            ? [
                                { text: 'Alberta', value: 'AB' },
                                { text: 'British Columbia', value: 'BC' },
                                { text: 'Manitoba', value: 'MB' },
                                { text: 'New Brunswick', value: 'NB' },
                                {
                                  text: 'Newfoundland and Labrador',
                                  value: 'NL'
                                },
                                { text: 'Nova Scotia', value: 'NS' },
                                { text: 'Ontario', value: 'ON' },
                                { text: 'Prince Edward Island', value: 'PE' },
                                { text: 'Quebec', value: 'QC' },
                                { text: 'Saskatchewan', value: 'SK' }
                              ]
                            : []
                        }
                        value={stateValues.province}
                        onChange={handleProvinceChange}
                      />
                    </div>
                    {stateValues.province && (
                      <div style={{ width: '40%', marginLeft: '1rem' }}>
                        <Select
                          label={labelRateCenterField}
                          placeholder="Please select..."
                          options={stateValues.rateCenterList}
                          value={stateValues.rateCenter}
                          onChange={e => {
                            setStateValues({
                              ...stateValues,
                              rateCenter: e.target.value
                            });
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
                {stateValues.type === 'NPANXX' && (
                  <>
                    <div style={{ width: '30%' }}>
                      <Input
                        type="tel"
                        label={labelNpaNxx}
                        pattern="[0-9]{6}"
                        maxLength="6"
                        value={stateValues.npanxx}
                        onChange={e => {
                          setStateValues({
                            ...stateValues,
                            npanxx: e.target.value
                          });
                        }}
                        tooltip={
                          <Tooltip copy="en">
                            6 digit: 1st and 4th digit cannot be 0 or 1
                          </Tooltip>
                        }
                      />
                    </div>
                    <div
                      style={{
                        width: '20%',
                        marginLeft: '1rem',
                        paddingTop: '1.8rem'
                      }}
                    >
                      <Button onClick={handleCheck}>{buttonCheck}</Button>
                    </div>
                    <div
                      style={{
                        width: '5%',
                        marginLeft: '1rem',
                        paddingTop: '2.25rem'
                      }}
                    >
                      <TextButton onClick={handleClearFields}>
                        {buttonClear}
                      </TextButton>
                    </div>
                  </>
                )}
              </div>
              <div
                style={{
                  marginTop: '1rem'
                }}
              >
                {stateValues.type === 'RC' && (
                  <Notification copy="en">
                    <Text bold>{labelNote}:</Text> {noteRateCenter}
                  </Notification>
                )}
                {status.isVisible && (
                  <StatusBar status={status} setStatus={setStatus} />
                )}
              </div>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default NewPhoneNumberCheck;
